import React, { useState, useMemo } from "react";
import { format, parseISO } from "date-fns";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/20/solid";

export function ScheduledVariantRuns({ workflow, runs }) {
  const [sortColumn, setSortColumn] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  console.log("workflow: ", workflow);
  console.log("runs: ", runs);

  const sortedRuns = useMemo(
    () => sortRuns(runs, sortColumn, sortDirection),
    [runs, sortColumn, sortDirection]
  );

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const columns = [
    { key: "created_at", label: "Created" },
    { key: "status", label: "Status" },
    { key: "stepsCompleted", label: "Steps Completed" },
  ];

  return (
    <div className="flow-root">
      {/* title */}
      <h2 className="text-lg font-semibold text-gray-700 mb-4">Runs</h2>
      {/* description of what is going on */}
      <p className="text-sm font-semibold text-gray-400 mb-4">
        Below are all of the runs associated with this variant.
      </p>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-300">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900 cursor-pointer group w-1/4"
                  onClick={() =>
                    column.key !== "stepsCompleted" && handleSort(column.key)
                  }
                >
                  <div className="flex items-center">
                    <span>{column.label}</span>
                    {column.key !== "stepsCompleted" && (
                      <span className="ml-2 flex-none rounded text-gray-900 group-hover:bg-gray-100">
                        {renderSortIcon(column.key, sortColumn, sortDirection)}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {sortedRuns.map((r, id) => (
              <tr key={id}>
                <td className="whitespace-nowrap py-4 px-3 text-sm text-gray-500">
                  {format(parseISO(r.created_at), "MMMM do, yyyy h:mm a")}
                </td>
                {/* status */}
                <td className="whitespace-nowrap py-4 px-3 text-sm text-gray-500">
                  <span className={getBadgeStyleFromStatus(r.status)}>
                    {r.status}
                  </span>
                </td>
                <td className="whitespace-nowrap py-4 px-3 text-sm text-gray-500">
                  {r.completedSteps ? r.completedSteps.length : 0}/
                  {workflow?.nodes ? workflow.nodes.length - 1 : 0}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function getBadgeStyleFromStatus(status) {
  const baseStyle =
    "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset";

  switch (status) {
    case "started":
      return `${baseStyle} bg-blue-50 text-blue-700 ring-blue-700/10`;
    case "running":
      return `${baseStyle} bg-yellow-50 text-yellow-800 ring-yellow-600/20`;
    case "completed":
      return `${baseStyle} bg-green-50 text-green-700 ring-green-600/20`;
    case "failed":
      return `${baseStyle} bg-red-50 text-red-700 ring-red-600/10`;
    default:
      return `${baseStyle} bg-gray-50 text-gray-600 ring-gray-500/10`;
  }
}

function sortRuns(runs, sortColumn, sortDirection) {
  if (!runs) return [];

  const sorted = [...runs].sort((a, b) => {
    if (sortColumn === "created_at") {
      return new Date(a.created_at) - new Date(b.created_at);
    }

    return 0;
  });

  return sortDirection === "asc" ? sorted : sorted.reverse();
}

function renderSortIcon(column, sortColumn, sortDirection) {
  return (
    <span
      className={`inline-flex pt-1 items-center justify-center w-5 h-5 transition-opacity duration-200 ease-in-out ${
        sortColumn === column
          ? "opacity-100"
          : "opacity-0 group-hover:opacity-100"
      }`}
    >
      {sortColumn === column && sortDirection === "asc" ? (
        <ChevronUpIcon className="w-4 h-4" />
      ) : (
        <ChevronDownIcon className="w-4 h-4" />
      )}
    </span>
  );
}
